/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import type { SystemStyleObject } from '@chakra-ui/theme-tools';

const sltDrawerVariants = [
    'hug',
    'tall',
    'full',
    'bottom-sheet',
    'bottom-sheet-full',
    'bottom-sheet_legacy',
    'bottom-sheet-full_legacy',
    'popover',
] as const;
type SLTDrawerVariant = (typeof sltDrawerVariants)[number];

const sharedStyleOverlay: SystemStyleObject = {
    background: 'slt-black-overlay',
    zIndex: 'modal',
};

const sharedStyleCloseButton: SystemStyleObject = {
    padding: 0,
    width: 6,
    height: 6,
    top: 4,
    right: 3,
    _focus: {
        boxShadow: 'none',
    },
};

const sharedStyleHeader: SystemStyleObject = {
    textStyle: 'slt-heading-2xl',
    padding: 4,
};

const sharedStyleBody: SystemStyleObject = {
    textStyle: 'slt-body-text-lg',
    paddingX: 4,
    paddingTop: 0,
    paddingBottom: 6,
};

const sharedStyleFooter: SystemStyleObject = {
    padding: 4,
    boxShadow: '0px -2px 10px 0px rgba(65, 64, 66, 0.14)',
};

const sharedStyleDialog: SystemStyleObject = {
    padding: 0,
    borderRadius: { base: '8px 8px 0px 0px', md: '0' },
    minWidth: { md: '372px', lg: '480px' },
};

const shared: SystemStyleObject = {
    overlay: sharedStyleOverlay,
    closeButton: sharedStyleCloseButton,
    header: sharedStyleHeader,
    body: sharedStyleBody,
    footer: sharedStyleFooter,
};

const bottomSheet: SystemStyleObject & { dialog: SystemStyleObject } = {
    ...shared,
    dialog: {
        ...sharedStyleDialog,
        borderRadius: 0,
        maxHeight: {
            base: 'calc(min(100dvh, 100vh, 100%) - 2rem)',
            md: 'min(100dvh, 100vh, 100%)',
        },
        minWidth: {
            md: '21.3rem',
            lg: '25.8rem',
            xl: '28.6rem',
        },
    },
};

const bottomSheetFull: SystemStyleObject & { dialog: SystemStyleObject } = {
    ...shared,
    dialog: {
        ...sharedStyleDialog,
        borderRadius: 0,
        maxHeight: 'min(100dvh, 100vh, 100%)',
        height: 'min(100dvh, 100vh, 100%)',
        minWidth: {
            md: '21.3rem',
            lg: '25.8rem',
            xl: '28.6rem',
        },
    },
};

const sltVariants: Record<SLTDrawerVariant, SystemStyleObject> = {
    hug: {
        ...shared,
        dialog: {
            ...sharedStyleDialog,
            maxHeight: {
                base: 'calc(100% - 32px)',
                md: '100%',
            },
        },
    },
    tall: {
        ...shared,
        dialog: {
            ...sharedStyleDialog,
            top: '32px',
        },
    },
    full: {
        ...shared,
        dialog: {
            ...sharedStyleDialog,
            height: '100%',
            borderRadius: 0,
        },
    },
    'bottom-sheet': {
        ...bottomSheet,
    },
    'bottom-sheet_legacy': {
        ...bottomSheet,
        dialog: {
            ...bottomSheet.dialog,
            maxHeight: {
                base: 'calc(100% - 2rem)',
                md: '100%',
            },
        },
    },
    'bottom-sheet-full': {
        ...bottomSheetFull,
    },
    'bottom-sheet-full_legacy': {
        ...bottomSheetFull,
        dialog: {
            ...bottomSheetFull.dialog,
            maxHeight: '100%',
            height: '100%',
        },
    },
    popover: {
        dialog: {
            width: '100%',
            gap: 4,
            backgroundColor: 'slt-beige.400',
            borderColor: 'white',
            borderRadius: { base: 0, sm: 16 },
            boxShadow: '0px 2px 10px 0px rgba(65, 64, 66, 0.14)',
        },
        header: {
            textStyle: 'body-large-2',
            backgroundColor: 'white',
            border: 'none',
            padding: 4,
            borderTopRadius: { base: 0, sm: 16 },
        },
        body: {
            textStyle: 'body-base',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            paddingTop: 0,
            paddingRight: 4,
            paddingBottom: 4,
            paddingLeft: 4,
        },
    },
};

export const Drawer = {
    variants: sltVariants,
    defaultProps: {
        variant: 'hug',
        placement: {
            base: 'bottom',
            md: '',
        },
    },
};
